import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import CommonModal from '../Components/CommonModal';
import { ConfirmModal as CommonConfirmModal } from '../Components/ConfirmModal';
import {
    getPlatformUsers, addPlatformUser, unassignRoleFromUser,
    getOrgMembers, addOrgMember, updateOrgMember, deleteOrgMember,
    getReportEmailRecipients, addReportEmailRecipient, updateReportEmailRecipient, deleteReportEmailRecipient,
} from '../services';
import { getUserFlags } from '../helper';
import CommonTable from '../Components/CommonTable';

// ─── Role display config ─────────────────────────────────────────────────────
const ROLE_META = {
    super_admin: { label: 'Super Admin', color: '#a855f7', bg: 'rgba(168,85,247,0.15)' },
    sub_admin: { label: 'Sub Admin', color: '#3b82f6', bg: 'rgba(59,130,246,0.15)' },
    tam: { label: 'TAM', color: '#0ea5e9', bg: 'rgba(14,165,233,0.15)' },
    mssp: { label: 'MSSP', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
    org_admin: { label: 'Org Admin', color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
    org_user: { label: 'Org User', color: '#94a3b8', bg: 'rgba(148,163,184,0.15)' },
    vendor: { label: 'Vendor', color: '#64748b', bg: 'rgba(100,116,139,0.15)' },
};

const PLATFORM_ROLE_OPTIONS = [
    { value: 'sub_admin', label: 'Sub Admin' },
    { value: 'tam', label: 'TAM (Technical Account Manager)' },
    { value: 'mssp', label: 'MSSP (Managed Security Provider)' },
];

const ORG_ROLE_OPTIONS = [
    { value: 'org_admin', label: 'Organization Admin' },
    { value: 'org_user', label: 'Organization User' },
];

const INPUT_STYLE = {
    background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9',
    borderRadius: '6px', fontSize: '13px', padding: '7px 10px', width: '100%',
    outline: 'none', fontFamily: "'Outfit', sans-serif",
};
const LABEL_STYLE = {
    color: '#ffffff', fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block',
};
const MODAL_HEADER_STYLE = { background: '#0f172a', borderBottom: 'none', fontFamily: "'Outfit', sans-serif" };
const MODAL_BODY_STYLE = { background: '#0f172a', fontFamily: "'Outfit', sans-serif" };
const MODAL_FOOTER_STYLE = { background: '#0f172a', borderTop: 'none', fontFamily: "'Outfit', sans-serif" };

// ─── Report-recipient response helpers ───────────────────────────────────────
// The list endpoint wraps the array as
//   { status, response_data: { data: { recipients: [...], count } } }
// but may also come back as a bare list or a DRF-paginated object — probe the
// known shapes and return the first array we find.
function extractRecipientList(res) {
    const s = res?.statusCode;
    if (Array.isArray(s)) return s;
    const candidates = [
        s?.response_data?.data?.recipients,
        s?.data?.recipients,
        s?.response_data?.data,
        s?.response_data?.results,
        s?.data,
        s?.results,
    ];
    return candidates.find(Array.isArray) || [];
}
// Success = no error field and no explicit failure status in the envelope.
function recipientOk(res) {
    if (res?.errors) return false;
    const s = res?.statusCode;
    if (s && (s.status === 0 || s.status_code === 0)) return false;
    return true;
}

// ─── Sub-components ──────────────────────────────────────────────────────────
function RoleBadge({ slug }) {
    const meta = ROLE_META[slug] || { label: slug || '—', color: '#94a3b8', bg: 'rgba(148,163,184,0.15)' };
    return (
        <span style={{
            display: 'inline-block', padding: '2px 10px', borderRadius: '12px',
            fontSize: '11px', fontWeight: 600, letterSpacing: '0.4px',
            color: '#ffffff', background: meta.bg, border: `1px solid ${meta.color}33`,
        }}>
            {meta.label}
        </span>
    );
}

function StatusBadge({ status }) {
    const active = String(status || '').toLowerCase() === 'active';
    return (
        <span style={{
            display: 'inline-block', padding: '2px 10px', borderRadius: '12px',
            fontSize: '11px', fontWeight: 600,
            color: active ? '#22c55e' : '#ef4444',
            background: active ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
            border: `1px solid ${active ? '#22c55e33' : '#ef444433'}`,
        }}>
            {active ? 'Active' : 'Inactive'}
        </span>
    );
}

function EmptyRow({ loading, colSpan, message }) {
    return (
        <tr>
            <td colSpan={colSpan} style={{ textAlign: 'center', color: '#475569', padding: '32px 0', fontSize: '13px' }}>
                {loading ? 'Loading…' : message}
            </td>
        </tr>
    );
}

function ConfirmModal({ show, title, body, onConfirm, onCancel, busy }) {
    return (
        <Modal show={show} onHide={onCancel} centered size="sm">
            <Modal.Header closeButton style={MODAL_HEADER_STYLE}>
                <Modal.Title style={{ color: '#ffffff', fontSize: '15px' }}>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ ...MODAL_BODY_STYLE, color: '#94a3b8', fontSize: '13px' }}>{body}</Modal.Body>
            <Modal.Footer style={MODAL_FOOTER_STYLE}>
                <Button variant="secondary" size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
                <Button variant="danger" size="sm" onClick={onConfirm} disabled={busy}>
                    {busy ? 'Removing…' : 'Confirm'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Section A — Platform Users  (super_admin only)
// ═══════════════════════════════════════════════════════════════════════════
function PlatformUsersSection() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [confirm, setConfirm] = useState(null); // { user, assignmentId }
    const [busy, setBusy] = useState(false);

    const [form, setForm] = useState({
        first_name: '', last_name: '', emailId: '', password: '', confirm_password: '', role_slug: 'sub_admin',
    });
    const [formErr, setFormErr] = useState({});
    const [addErr, setAddErr] = useState('');

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const res = await getPlatformUsers();
            const d = res?.statusCode?.data || res?.statusCode?.response_data?.data || [];
            setUsers(Array.isArray(d) ? d : []);
        } catch { setError('Failed to load platform users.'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const validate = () => {
        const e = {};
        if (!form.first_name.trim()) e.first_name = 'Required';
        if (!form.last_name.trim()) e.last_name = 'Required';
        if (!form.emailId.trim()) e.emailId = 'Required';
        if (!form.password) e.password = 'Required';
        if (form.password !== form.confirm_password) e.confirm_password = 'Passwords do not match';
        setFormErr(e);
        return !Object.keys(e).length;
    };

    const handleAdd = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setBusy(true); setAddErr('');
        try {
            const res = await addPlatformUser(form);
            const ok = res?.statusCode?.status === 1 || res?.statusCode?.status_code === 1;
            if (ok) {
                setShowAdd(false);
                setForm({ first_name: '', last_name: '', emailId: '', password: '', confirm_password: '', role_slug: 'sub_admin' });
                load();
            } else {
                setAddErr(res?.statusCode?.error?.message || res?.errors?.message || 'Failed to create user.');
            }
        } catch { setAddErr('An unexpected error occurred.'); }
        finally { setBusy(false); }
    };

    const handleUnassign = async () => {
        if (!confirm) return;
        setBusy(true);
        try {
            await unassignRoleFromUser(confirm.assignmentId);
            setConfirm(null); load();
        } catch { setConfirm(null); }
        finally { setBusy(false); }
    };

    return (
        <div className="um-section">
            <div className="um-section-header">
                <div>
                    <h3 className="um-section-title">Platform Users</h3>
                    <p className="um-section-sub">Sub Admins, TAMs, and MSSPs managing the platform.</p>
                </div>
                <button className="um-btn-primary" onClick={() => { setShowAdd(true); setAddErr(''); setFormErr({}); }}>
                    + Add Platform User
                </button>
            </div>

            {error && <p className="um-inline-error">{error}</p>}

            <div className="um-table-wrap">
                <table className="um-table">
                    <thead>
                        <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                        {loading || !users.length
                            ? <EmptyRow loading={loading} colSpan={5} message="No platform users found." />
                            : users.map(u => (
                                <tr key={u.id}>
                                    <td>{u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—'}</td>
                                    <td className="um-cell-muted">{u.emailId}</td>
                                    <td>
                                        {u.role_info
                                            ? <RoleBadge slug={u.role_info.slug} />
                                            : <span className="um-cell-dim">No role</span>}
                                    </td>
                                    <td><StatusBadge status={u.status || 'Active'} /></td>
                                    <td>
                                        {u.role_info?.assignment_id && (
                                            <button className="um-btn-danger-sm" onClick={() => setConfirm({ user: u, assignmentId: u.role_info.assignment_id })}>
                                                Unassign Role
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            {/* Add modal */}
            <Modal show={showAdd} onHide={() => setShowAdd(false)} centered>
                <Modal.Header closeButton style={MODAL_HEADER_STYLE}>
                    <Modal.Title style={{ color: '#ffffff', fontSize: '16px' }}>Add Platform User</Modal.Title>
                </Modal.Header>
                <Modal.Body style={MODAL_BODY_STYLE}>
                    <Form onSubmit={handleAdd}>
                        <div className="um-form-row">
                            <div>
                                <label style={LABEL_STYLE}>First Name</label>
                                <input style={INPUT_STYLE} value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
                                {formErr.first_name && <p className="um-field-err">{formErr.first_name}</p>}
                            </div>
                            <div>
                                <label style={LABEL_STYLE}>Last Name</label>
                                <input style={INPUT_STYLE} value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
                                {formErr.last_name && <p className="um-field-err">{formErr.last_name}</p>}
                            </div>
                        </div>
                        <div className="um-form-field">
                            <label style={LABEL_STYLE}>Email</label>
                            <input type="email" style={INPUT_STYLE} value={form.emailId} onChange={e => setForm(f => ({ ...f, emailId: e.target.value }))} />
                            {formErr.emailId && <p className="um-field-err">{formErr.emailId}</p>}
                        </div>
                        <div className="um-form-field">
                            <label style={LABEL_STYLE}>Role</label>
                            <select style={INPUT_STYLE} value={form.role_slug} onChange={e => setForm(f => ({ ...f, role_slug: e.target.value }))}>
                                {PLATFORM_ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <div className="um-form-field">
                            <label style={LABEL_STYLE}>Password</label>
                            <input type="password" style={INPUT_STYLE} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
                            {formErr.password && <p className="um-field-err">{formErr.password}</p>}
                        </div>
                        <div className="um-form-field">
                            <label style={LABEL_STYLE}>Confirm Password</label>
                            <input type="password" style={INPUT_STYLE} value={form.confirm_password} onChange={e => setForm(f => ({ ...f, confirm_password: e.target.value }))} />
                            {formErr.confirm_password && <p className="um-field-err">{formErr.confirm_password}</p>}
                        </div>
                        {addErr && <p className="um-inline-error" style={{ marginTop: '10px' }}>{addErr}</p>}
                    </Form>
                </Modal.Body>
                <Modal.Footer style={MODAL_FOOTER_STYLE}>
                    <Button variant="secondary" size="sm" onClick={() => setShowAdd(false)} disabled={busy}>Cancel</Button>
                    <Button variant="primary" size="sm" onClick={handleAdd} disabled={busy}>
                        {busy ? 'Creating…' : 'Create User'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <ConfirmModal
                show={!!confirm}
                title="Unassign Role"
                body={`Remove the ${confirm?.user?.role_info?.role_name} role from ${confirm?.user?.full_name || confirm?.user?.emailId}? The account remains active with no platform role.`}
                onConfirm={handleUnassign}
                onCancel={() => setConfirm(null)}
                busy={busy}
            />
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Section B — Org Members  (org_admin, mssp, tam, super_admin proxying)
// ═══════════════════════════════════════════════════════════════════════════
function OrgMembersSection({ organizationId }) {
    const flags = getUserFlags();
    const currentEmail = (localStorage.getItem('email') || '').toLowerCase();

    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [delItem, setDelItem] = useState(null);
    const [busy, setBusy] = useState(false);

    const [addForm, setAddForm] = useState({ first_name: '', last_name: '', emailId: '', password: '', role_slug: 'org_user' });
    const [addErr, setAddErr] = useState('');
    const [addFErr, setAddFErr] = useState({});

    const [editForm, setEditForm] = useState({ first_name: '', last_name: '', role_slug: 'org_user' });
    const [editErr, setEditErr] = useState('');

    // Report email recipient — add
    const [showRecipient, setShowRecipient] = useState(false);
    const [recipientErr, setRecipientErr] = useState('');

    // Report email recipient — list / edit / delete
    const [showRecipientList, setShowRecipientList] = useState(false);
    const [recipients, setRecipients] = useState([]);
    const [recipientsLoading, setRecipientsLoading] = useState(false);
    const [recipientsError, setRecipientsError] = useState('');
    const [editRecipient, setEditRecipient] = useState(null); // record being edited
    const [editRecipientErr, setEditRecipientErr] = useState('');
    const [delRecipient, setDelRecipient] = useState(null);
    const [recipientBusy, setRecipientBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const res = await getOrgMembers(organizationId);
            const d = res?.statusCode?.data || res?.statusCode?.response_data?.data || [];
            setMembers(Array.isArray(d) ? d : []);
        } catch { setError('Failed to load organization members.'); }
        finally { setLoading(false); }
    }, [organizationId]);

    useEffect(() => { load(); }, [load]);

    const validateAdd = () => {
        const e = {};
        if (!addForm.first_name.trim()) e.first_name = 'Required';
        if (!addForm.last_name.trim()) e.last_name = 'Required';
        if (!addForm.emailId.trim()) e.emailId = 'Required';
        if (!addForm.password) e.password = 'Required';
        setAddFErr(e);
        return !Object.keys(e).length;
    };

    const handleAdd = async (ev) => {
        ev.preventDefault();
        if (!validateAdd()) return;
        setBusy(true); setAddErr('');
        try {
            const res = await addOrgMember(addForm, organizationId);
            const ok = res?.statusCode?.status === 1 || res?.statusCode?.status_code === 1;
            if (ok) {
                setShowAdd(false);
                setAddForm({ first_name: '', last_name: '', emailId: '', password: '', role_slug: 'org_user' });
                load();
            } else {
                setAddErr(res?.statusCode?.error?.message || res?.errors?.message || 'Failed to add member.');
            }
        } catch { setAddErr('An unexpected error occurred.'); }
        finally { setBusy(false); }
    };

    const openEdit = (m) => {
        setEditItem(m);
        setEditForm({
            first_name: m.first_name || '',
            last_name: m.last_name || '',
            role_slug: m.org_role || m.org_role_info?.slug || 'org_user',
        });
        setEditErr('');
    };

    const handleEdit = async (ev) => {
        ev.preventDefault();
        if (!editItem) return;
        setBusy(true); setEditErr('');
        try {
            const res = await updateOrgMember(editItem.id, editForm, organizationId);
            const ok = res?.statusCode?.status === 1 || res?.statusCode?.status_code === 1;
            if (ok) { setEditItem(null); load(); }
            else setEditErr(res?.statusCode?.error?.message || res?.errors?.message || 'Failed to update member.');
        } catch { setEditErr('An unexpected error occurred.'); }
        finally { setBusy(false); }
    };

    const handleDelete = async () => {
        if (!delItem) return;
        setBusy(true);
        try { await deleteOrgMember(delItem.id, organizationId); setDelItem(null); load(); }
        catch { setDelItem(null); }
        finally { setBusy(false); }
    };

    const loadRecipients = useCallback(async () => {
        setRecipientsLoading(true); setRecipientsError('');
        try {
            const res = await getReportEmailRecipients();
            setRecipients(extractRecipientList(res));
        } catch { setRecipientsError('Failed to load recipients.'); }
        finally { setRecipientsLoading(false); }
    }, []);

    const openRecipientList = () => { setShowRecipientList(true); loadRecipients(); };

    const REPORT_TYPE_OPTIONS = [
        { value: 'cxo_summary', label: 'CXO Summary' },
        { value: 'brand_risk', label: 'Brand Risk' },
    ];

    const handleAddRecipient = async (data) => {
        setBusy(true); setRecipientErr('');
        try {
            const payload = {
                email: (data.email || '').trim(),
                report_types: data.report_types || [],
                extra_data: { name: (data.name || '').trim(), designation: (data.designation || '').trim() },
                is_active: data.is_active,
            };
            const res = await addReportEmailRecipient(payload);
            if (recipientOk(res)) {
                setShowRecipient(false);
                if (showRecipientList) loadRecipients();
            } else {
                setRecipientErr(res?.statusCode?.error?.message || res?.errors?.message || 'Failed to add recipient.');
            }
        } catch (error) {
            setRecipientErr(error?.response?.data?.error?.message || 'An unexpected error occurred.');
        }
        finally { setBusy(false); }
    };

    const handleEditRecipient = async (data) => {
        if (!editRecipient) return;
        setRecipientBusy(true); setEditRecipientErr('');
        try {
            const payload = {
                email: (data.email || '').trim(),
                report_types: data.report_types || [],
                extra_data: { name: (data.name || '').trim(), designation: (data.designation || '').trim() },
                is_active: data.is_active,
            };
            const res = await updateReportEmailRecipient(editRecipient.id, payload);
            if (recipientOk(res)) { setEditRecipient(null); loadRecipients(); }
            else setEditRecipientErr(res?.statusCode?.error?.message || res?.errors?.message || 'Failed to update recipient.');
        } catch (error) {
            setEditRecipientErr(error?.response?.data?.error?.message || 'An unexpected error occurred.');
        }
        finally { setRecipientBusy(false); }
    };

    const handleDeleteRecipient = async () => {
        if (!delRecipient) return;
        setRecipientBusy(true);
        try { await deleteReportEmailRecipient(delRecipient.id); setDelRecipient(null); loadRecipients(); }
        catch { setDelRecipient(null); }
        finally { setRecipientBusy(false); }
    };

    const isSelf = (m) => (m.emailId || '').toLowerCase() === currentEmail;
    const getRoleSlug = (m) => m.org_role || m.org_role_info?.slug || '';
    const canManage = flags.is_platform_admin || flags.is_org_admin || flags.role_slug === 'mssp' || flags.role_slug === 'tam';

    return (
        <div className="um-section">
            <div className="um-section-header">
                <div>
                    <h3 className="um-section-title">Organization Members</h3>
                    <p className="um-section-sub">Admins and users within this organization.</p>
                </div>
                {canManage && (
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button className="um-btn-ghost" onClick={openRecipientList}>
                            Recipient List
                        </button>
                        <button className="um-btn-primary" onClick={() => { setShowRecipient(true); setRecipientErr(''); }}>
                            + Report Recipient
                        </button>
                        <button className="um-btn-primary" onClick={() => { setShowAdd(true); setAddErr(''); setAddFErr({}); }}>
                            + Invite Member
                        </button>
                    </div>
                )}
            </div>

            {error && <p className="um-inline-error">{error}</p>}

            <div className="um-table-wrap">
                <table className="um-table">
                    <thead>
                        <tr><th>Name</th><th>Email</th><th>Role</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                        {loading || !members.length
                            ? <EmptyRow loading={loading} colSpan={4} message="No members found." />
                            : members.map(m => (
                                <tr key={m.id}>
                                    <td>
                                        {`${m.first_name || ''} ${m.last_name || ''}`.trim() || '—'}
                                        {isSelf(m) && <span className="um-you-tag">You</span>}
                                    </td>
                                    <td className="um-cell-muted">{m.emailId}</td>
                                    <td><RoleBadge slug={getRoleSlug(m)} /></td>
                                    <td>
                                        {canManage && !isSelf(m) && (
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                <button className="um-btn-ghost-sm" onClick={() => openEdit(m)}>Edit</button>
                                                <button className="um-btn-danger-sm" onClick={() => setDelItem(m)}>Remove</button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            {/* Add modal */}
            <Modal show={showAdd} onHide={() => setShowAdd(false)} centered>
                <Modal.Header closeButton style={MODAL_HEADER_STYLE}>
                    <Modal.Title style={{ color: '#ffffff', fontSize: '16px' }}>Invite Member</Modal.Title>
                </Modal.Header>
                <Modal.Body style={MODAL_BODY_STYLE}>
                    <Form onSubmit={handleAdd}>
                        <div className="um-form-row">
                            <div>
                                <label style={LABEL_STYLE}>First Name</label>
                                <input style={INPUT_STYLE} value={addForm.first_name} onChange={e => setAddForm(f => ({ ...f, first_name: e.target.value }))} />
                                {addFErr.first_name && <p className="um-field-err">{addFErr.first_name}</p>}
                            </div>
                            <div>
                                <label style={LABEL_STYLE}>Last Name</label>
                                <input style={INPUT_STYLE} value={addForm.last_name} onChange={e => setAddForm(f => ({ ...f, last_name: e.target.value }))} />
                                {addFErr.last_name && <p className="um-field-err">{addFErr.last_name}</p>}
                            </div>
                        </div>
                        <div className="um-form-field">
                            <label style={LABEL_STYLE}>Email</label>
                            <input type="email" style={INPUT_STYLE} value={addForm.emailId} onChange={e => setAddForm(f => ({ ...f, emailId: e.target.value }))} />
                            {addFErr.emailId && <p className="um-field-err">{addFErr.emailId}</p>}
                        </div>
                        <div className="um-form-field">
                            <label style={LABEL_STYLE}>Role</label>
                            <select style={INPUT_STYLE} value={addForm.role_slug} onChange={e => setAddForm(f => ({ ...f, role_slug: e.target.value }))}>
                                {ORG_ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <div className="um-form-field">
                            <label style={LABEL_STYLE}>Temporary Password</label>
                            <input type="password" style={INPUT_STYLE} value={addForm.password} onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))} />
                            {addFErr.password && <p className="um-field-err">{addFErr.password}</p>}
                        </div>
                        {addErr && <p className="um-inline-error" style={{ marginTop: '10px' }}>{addErr}</p>}
                    </Form>
                </Modal.Body>
                <Modal.Footer style={MODAL_FOOTER_STYLE}>
                    <Button variant="secondary" size="sm" onClick={() => setShowAdd(false)} disabled={busy}>Cancel</Button>
                    <Button variant="primary" size="sm" onClick={handleAdd} disabled={busy}>
                        {busy ? 'Inviting…' : 'Invite'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Report recipient modal */}
            <CommonModal
                show={showRecipient}
                onHide={() => setShowRecipient(false)}
                mid
                title="Add Report Recipient"
                subtitle="They'll receive scheduled reports by email."
                fields={[
                    { name: 'email', label: 'Email', type: 'email', placeholder: 'name@company.com', required: true },
                    { name: 'name', label: 'Name', type: 'text', half: true, required: true },
                    { name: 'designation', label: 'Designation', type: 'text', half: true, placeholder: 'e.g. Developer' },
                    { name: 'report_types', label: 'Report Types', type: 'multiselect', half: true, placeholder: 'Select report types', options: REPORT_TYPE_OPTIONS },
                    { name: 'is_active', label: 'Active', type: 'checkbox', half: true, },
                ]}
                defaultValues={{ email: '', name: '', designation: '', report_types: [], is_active: true }}
                onSubmit={handleAddRecipient}
                error={recipientErr}
                buttons={[
                    { text: 'Cancel', variant: 'secondary', onClick: () => setShowRecipient(false), disabled: busy },
                    { text: 'Add Recipient', variant: 'primary', type: 'submit', loading: busy, loadingText: 'Saving…' },
                ]}
            />

            {/* Report recipient list modal */}
            <CommonModal
                show={showRecipientList}
                onHide={() => setShowRecipientList(false)}
                title="Report Recipients"
                subtitle="People who receive scheduled reports by email."
                size="xl"
                buttons={[{ text: 'Close', variant: 'secondary', onClick: () => setShowRecipientList(false) }]}
            >
                {recipientsError ? (
                    <p className="cmn-modal-error">{recipientsError}</p>
                ) : recipientsLoading ? (
                    <p style={{ color: '#94a3b8', textAlign: 'center', padding: '24px 0', fontSize: '13px' }}>Loading…</p>
                ) : (
                    <CommonTable
                        headers={['Email', 'Name', 'Designation', 'Report Types', 'Status', 'Actions']}
                        rows={recipients.map(r => ({
                            data: [
                                r.email || '—',
                                r.extra_data?.name || '—',
                                r.extra_data?.designation || '—',
                                (r.report_types && r.report_types.length) ? r.report_types.join(', ') : 'All',
                                <StatusBadge status={r.is_active ? 'active' : 'inactive'} />,
                            ],
                            _record: r,
                        }))}
                        actions={(row) => (
                            <div style={{ display: 'flex', gap: '6px' }}>
                                <button type="button" className="um-btn-ghost-sm" onClick={() => { setEditRecipient(row._record); setEditRecipientErr(''); }}>Edit</button>
                                <button type="button" className="um-btn-danger-sm" onClick={() => setDelRecipient(row._record)}>Remove</button>
                            </div>
                        )}
                        noPadding
                        noScroll
                    />
                )}
            </CommonModal>

            {/* Report recipient edit modal */}
            <CommonModal
                show={!!editRecipient}
                onHide={() => setEditRecipient(null)}
                mid
                title="Edit Report Recipient"
                subtitle="Update this recipient's details."
                fields={[
                    { name: 'email', label: 'Email', type: 'email', placeholder: 'name@company.com', required: true },
                    { name: 'name', label: 'Name', type: 'text', half: true, required: true },
                    { name: 'designation', label: 'Designation', type: 'text', half: true, placeholder: 'e.g. Developer' },
                    { name: 'report_types', label: 'Report Types', type: 'multiselect', half: true, placeholder: 'Select report types', options: REPORT_TYPE_OPTIONS },
                    { name: 'is_active', label: 'Active', type: 'checkbox' },
                ]}
                defaultValues={{
                    email: editRecipient?.email || '',
                    name: editRecipient?.extra_data?.name || '',
                    designation: editRecipient?.extra_data?.designation || '',
                    report_types: editRecipient?.report_types || [],
                    is_active: editRecipient?.is_active ?? true,
                }}
                onSubmit={handleEditRecipient}
                error={editRecipientErr}
                buttons={[
                    { text: 'Cancel', variant: 'secondary', onClick: () => setEditRecipient(null), disabled: recipientBusy },
                    { text: 'Save Changes', variant: 'primary', type: 'submit', loading: recipientBusy, loadingText: 'Saving…' },
                ]}
            />

            {/* Report recipient delete confirm — shared ConfirmModal */}
            <CommonConfirmModal
                state={delRecipient ? {
                    title: 'Remove Recipient',
                    message: `Remove ${delRecipient?.email || 'this recipient'} from report recipients? They'll stop receiving scheduled reports.`,
                    confirmLabel: recipientBusy ? 'Removing…' : 'Remove',
                    cancelLabel: 'Cancel',
                    danger: true,
                } : null}
                onClose={(ok) => { if (ok) handleDeleteRecipient(); else setDelRecipient(null); }}
            />

            {/* Edit modal */}
            <Modal show={!!editItem} onHide={() => setEditItem(null)} centered>
                <Modal.Header closeButton style={MODAL_HEADER_STYLE}>
                    <Modal.Title style={{ color: '#ffffff', fontSize: '16px' }}>
                        Edit — {editItem ? (`${editItem.first_name || ''} ${editItem.last_name || ''}`.trim() || editItem.emailId) : ''}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={MODAL_BODY_STYLE}>
                    <Form onSubmit={handleEdit}>
                        <div className="um-form-row">
                            <div>
                                <label style={LABEL_STYLE}>First Name</label>
                                <input style={INPUT_STYLE} value={editForm.first_name} onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))} />
                            </div>
                            <div>
                                <label style={LABEL_STYLE}>Last Name</label>
                                <input style={INPUT_STYLE} value={editForm.last_name} onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))} />
                            </div>
                        </div>
                        <div className="um-form-field">
                            <label style={LABEL_STYLE}>Role</label>
                            <select style={INPUT_STYLE} value={editForm.role_slug} onChange={e => setEditForm(f => ({ ...f, role_slug: e.target.value }))}>
                                {ORG_ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        {editErr && <p className="um-inline-error" style={{ marginTop: '10px' }}>{editErr}</p>}
                    </Form>
                </Modal.Body>
                <Modal.Footer style={MODAL_FOOTER_STYLE}>
                    <Button variant="secondary" size="sm" onClick={() => setEditItem(null)} disabled={busy}>Cancel</Button>
                    <Button variant="primary" size="sm" onClick={handleEdit} disabled={busy}>
                        {busy ? 'Saving…' : 'Save Changes'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <ConfirmModal
                show={!!delItem}
                title="Remove Member"
                body={`Remove ${delItem ? (`${delItem.first_name || ''} ${delItem.last_name || ''}`.trim() || delItem.emailId) : ''} from this organization? Their access is revoked immediately.`}
                onConfirm={handleDelete}
                onCancel={() => setDelItem(null)}
                busy={busy}
            />
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Root component
// ═══════════════════════════════════════════════════════════════════════════
function UserManagement() {
    const flags = getUserFlags();
    const isSuperAdmin = flags.role_slug === 'super_admin';
    const canManageOrg = flags.is_org_admin || flags.role_slug === 'mssp' || flags.role_slug === 'tam' || flags.is_platform_admin;

    const organizationId = sessionStorage.getItem('organization_id') || localStorage.getItem('organization_id') || null;

    // Forward 406 / 403 custom events to alert (swap for toast library if available)
    useEffect(() => {
        const onPlan = (e) => alert(`Upgrade Required: ${e.detail?.message}`);
        const onRbac = (e) => alert(`Permission Denied: ${e.detail?.message}`);
        window.addEventListener('plan_gate_denied', onPlan);
        window.addEventListener('rbac_permission_denied', onRbac);
        return () => {
            window.removeEventListener('plan_gate_denied', onPlan);
            window.removeEventListener('rbac_permission_denied', onRbac);
        };
    }, []);

    if (!isSuperAdmin && !canManageOrg) {
        return (
            <div className="um-no-access">
                <p>You do not have permission to manage users.</p>
            </div>
        );
    }

    return (
        <div className="um-container">
            {/* Platform Users — super_admin only, per spec */}
            {isSuperAdmin && <PlatformUsersSection />}

            {/* Org Members — all roles with org management rights */}
            {canManageOrg && (
                <OrgMembersSection organizationId={isSuperAdmin ? null : organizationId} />
            )}
        </div>
    );
}
export default UserManagement;
